"use client";

import { SOfferDto, SProduct } from "@/__generated__/graphql";
import { Box, Card, Chip, Stack, Typography, Link } from "@mui/material";
import ProductCardColors from "./ProductCardColors";
import ProductCardOfferType from "./ProductCardOfferType";
import { useBreakPoints } from "@/lib/useBreakPoints";
import NextLink from "next/link";
import ProductCardInBasketLogo from "./ProductCardInBasketLogo";
import React, { useEffect, useRef } from "react";
import ProductCardSnappPayIcon from "./ProductCardSnappPayIcon";
import ProductMediaOverlay from "./ProductMediaOverlay";
import {
  DiscoverySurface,
  trackProductDiscovery,
} from "@/lib/productDiscovery/trackProductDiscovery";

type Props = {
  product: SProduct;
  linkQuery?: string;
  /** Homepage / listing surface for discovery analytics; omit to skip tracking */
  surface?: DiscoverySurface;
  position?: number;
};
function arePropsEqual<P>(prevProps: Readonly<P>, nextProps: Readonly<P>): boolean {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
}
function ProductCardPrice({ variant }: { variant: NonNullable<SProduct["variant"]> }) {
  const listPrice = Number(variant.cashPrice ?? variant.price);
  const offerUnit = variant.offer ? Number(variant.offer.cashPrice ?? variant.offer.price) : null;
  const hasDiscount = offerUnit != null && offerUnit > 0 && offerUnit < listPrice;
  const percent = hasDiscount ? Math.round(100 - (offerUnit / listPrice) * 100) : null;
  const payPrice = hasDiscount && offerUnit != null ? offerUnit : listPrice;

  if (!hasDiscount) {
    return (
      <Typography fontWeight={700} component={"section"} variant="h6">
        {payPrice.toLocaleString()}
        <Typography sx={{ mx: 0.3 }} color={"grey.500"} component={"strong"} variant="h6">
          تومان
        </Typography>
      </Typography>
    );
  }

  return (
    <Stack alignItems={"end"}>
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
        <Typography component={"s"} variant="body2" color={"grey.400"}>
          {listPrice.toLocaleString()}
          <Typography sx={{ mx: 0.2 }} component={"small"} variant="body2">
            تومان
          </Typography>
        </Typography>
        <Box
          sx={{
            backgroundColor: "error.100",
            borderRadius: 4,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ mx: 1, color: "white.main" }} variant="body2" dir="ltr" lineHeight={0}>
            {`${percent} %`}
          </Typography>
        </Box>
      </Stack>
      <Typography fontWeight={700} variant="h6" component={"section"}>
        {payPrice.toLocaleString()}{" "}
        <Typography sx={{ mx: 0.3 }} color={"grey.500"} component={"strong"} variant="h6">
          تومان
        </Typography>
      </Typography>
    </Stack>
  );
}

function ProductCard({ product, linkQuery, surface, position = 0 }: Props) {
  const { mdAndDown } = useBreakPoints();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const impressedRef = useRef(false);

  const video = product.video;

  useEffect(() => {
    if (!surface || !product.id || impressedRef.current) return;
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || impressedRef.current) return;
        impressedRef.current = true;
        trackProductDiscovery({
          productId: product.id,
          surface,
          type: "impression",
          position,
        });
        observer.disconnect();
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [product.id, surface, position]);

  const borderRadius = { xs: 2, sm: 4 };
  return (
    <Card ref={rootRef} sx={{ borderRadius, height: "100%", background: "transparent" }} elevation={0}>
      <Link
        component={NextLink}
        // disableTouchRipple={true}
        // disableRipple={true}
        underline="none"
        target={mdAndDown ? "_self" : "_blank"}
        href={`/products/${product.code}/${product.title.replaceAll(" ", "-")}${linkQuery ? `?${linkQuery}` : ""}`}
        scroll={true}
        onClick={() => {
          if (!surface || !product.id) return;
          trackProductDiscovery({
            productId: product.id,
            surface,
            type: "click",
            position,
          });
        }}
        sx={{
          "& .productImage": {
            position: "relative",
            overflow: "hidden", // Ensures the overlay stays within the image bounds
          },
          "& .productImage:after": {
            content: '""', // Necessary for the :after pseudo-element
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0)", // Fully transparent by default
            zIndex: 1,
            transition: "all .1s ease-in-out", // Smooth transition for hover effects
            pointerEvents: "none", // Overlay won't block interactions
          },
          "&:hover .productImage:after": {
            backgroundColor: "rgba(0, 0, 0, 0.03)", // Semi-transparent black on hover
          },
          borderRadius,
          height: "100%",
          display: "flex",
          alignItems: "normal",
          // justifyContent: "start",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            borderRadius,
            overflow: "hidden",
          }}
        >
          {product.mainImage && (
            <Box className={"productImage"} sx={{ position: "relative", aspectRatio: 1 / 1.02, width: "100%" }}>
              {/* Vertical dots on top-left */}
              <ProductCardColors colors={product.colors} />
              <ProductCardOfferType offer={product.variant?.offer as SOfferDto} />
              <ProductCardInBasketLogo productId={product.id} />
              <ProductMediaOverlay imageSrc={product.mainImage?.address} alt={product.mainImage?.alt || product.title} videoSrc={video} />
              {/* <ProductImageComponent src={product.mainImage?.address} alt={product.mainImage?.alt || product.title} video={video}/> */}
            </Box>
          )}
          <ProductCardSnappPayIcon variant={product.variant} off={Boolean(!product.variant)} />
        </Box>

        <Box sx={{ py: 1 }}>
          <Typography
            variant="body2"
            component={"h3"}
            whiteSpace={"wrap"}
            sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {product.title}
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"end"} justifyContent={"space-between"} mt={"auto"} mx={0.2}>
          {product.variant ? (
            <ProductCardPrice variant={product.variant} />
          ) : (
            <Chip
              sx={{ backgroundColor: "grey.50" }}
              label={
                <Typography variant="body2" color={"grey.700"}>
                  ناموجود
                </Typography>
              }
            />
          )}
        </Stack>
      </Link>
    </Card>
  );
}
export default React.memo(ProductCard, arePropsEqual);
